import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';
import { inject, Injectable } from '@angular/core';
import * as auth from '@angular/fire/auth';
import { MapHlp } from 'src/lib/fliko/helpers/map.helper';
import { CheckDomainRes, RequestAccessCtx } from 'src/lib/fliko/models/http/ssoRequest.data';
import { Collection } from 'src/lib/fliko/types/database.type';
import { ContextService } from 'src/app/view/user-context/services/context.service';
import { AsyncHlp } from '../helpers/async.helper';
import { FunctionsApiService } from './functions-api.service';
import { SentryService } from './sentry.service';

type OnLogin = ((value: auth.UserCredential) => void | PromiseLike<void>) | null | undefined;
type OnLogInFailed = ((value: any) => void | PromiseLike<void>) | null | undefined;

@Injectable({ providedIn: 'root' })
export class AuthService {
  private auth = inject(auth.Auth);

  private currentUser = new BehaviorSubject<auth.User | null>(null);
  private claims = new BehaviorSubject<Collection<any> | null>(null);
  private logoutSubject = new Subject<void>();
  private _hasLoggedAfterLoad = false;

  private microsoftProviderMap = new Map<string, auth.OAuthProvider>();
  public readonly logout$: Observable<void> = this.logoutSubject.asObservable();

  /** Initialize Microsoft (FlikoTest) provider */
  private initializeMicrosoftProvider(providerId: string): auth.OAuthProvider {
    const provider = new auth.OAuthProvider(providerId);
    provider.addScope('email');
    provider.addScope('profile');
    provider.addScope('openid');
    return provider;
  }

  constructor(
    private sentrySrv: SentryService,
    private functionsApi: FunctionsApiService,
    private ctxSrv: ContextService
  ) {
    auth.onAuthStateChanged(this.auth, (user) => {
      this.currentUser.next(user);
      this.ctxSrv.setUser(user?.uid ?? null);
      this.sentrySrv.setUser(user?.uid ?? null);
      user?.getIdTokenResult()?.then((token) => this.claims.next(token?.claims ?? null));
    });
  }

  public async logIn(email: string, passwd: string, accept?: OnLogin, reject?: OnLogInFailed): Promise<void> {
    this.recordLoginAttempt(email);
    await auth
      .signInWithEmailAndPassword(this.auth, email, passwd)
      .then(
        (val) => {
          this.setupAfterLogin(val.user.uid);
          if (accept) accept(val);
        },
        (val) => {
          this.setupAfterLogout();
          if (reject) reject(val);
        }
      )
      .catch((error) => console.warn(`${email}, code: ${error.code}, message: ${error.message}`));
  }

  /**
   * Sign in with Microsoft SSO using Firebase OIDC Provider (Azure AD / Entra ID)
   * Uses popup flow to keep user on the same page
   * @param {string}             email    - User email from login input
   * @param {string | undefined} tenantId - Microsoft tenant ID (optional)
   * @return {Promise<auth.UserCredential>}
   */
  public async signInWithMicrosoftPopup(email: string, providerId: string, tenantId?: string): Promise<auth.UserCredential> {
    // ? Set custom parameters for Azure AD login
    const customParams: auth.CustomParameters = { prompt: 'select_account', login_hint: email };

    // ? If tenant ID is provided, add it to enforce tenant-specific login
    if (tenantId) customParams.tenant = tenantId;
    const microsoftOAuthProvider = MapHlp.getSet(this.microsoftProviderMap, providerId, () =>
      this.initializeMicrosoftProvider(providerId)
    );

    microsoftOAuthProvider.setCustomParameters(customParams);
    this.recordLoginAttempt(email);
    const result = await auth.signInWithPopup(this.auth, microsoftOAuthProvider);
    this.setupAfterLogin(result.user.uid);

    return result;
  }

  /**
   * ! ONLY USED AFTER SSO LOGIN AFTER WHITELIST /
   * Setup user data after whitelist check step - to have certainty that user exists in database
   * @param {string} userId
   */
  public updateAfterSso(userId: string): void {
    this.setupAfterLogin(userId);
  }

  /**
   * Setup some user data after login
   * @param {string} uid user id
   */
  private setupAfterLogin(uid: string): void {
    this._hasLoggedAfterLoad = true;
    this.ctxSrv.setUser(uid);
    this.sentrySrv.setUser(uid);
  }

  private recordLoginAttempt(email: string): void {
    void this.functionsApi
      .callFunction<boolean>('recordLoginAttempt', { email }, (error) => this.sentrySrv.captureError(error))
      .catch((error) => this.sentrySrv.captureError(error));
  }

  /**
   * Reset user data after logout
   */
  private setupAfterLogout(): void {
    this.sentrySrv.setUser(null);
    this.ctxSrv.setUser(null);
    this.currentUser.next(null);
  }

  public async logOut(): Promise<void> {
    // ! Firstly clear database context, to avoid "permission denied" errors, than log out from Firebase
    this.setupAfterLogout();
    this.logoutSubject.next();
    // HACK: authStateChanged still returns old userId!!!!
    await this.auth.signOut();
  }

  public async verifyUserCredentials(email: string, password: string): Promise<auth.UserCredential | null> {
    let user: auth.UserCredential | null = null;
    if (this.currentUser.value) {
      const credentials = auth.EmailAuthProvider.credential(email, password);
      user = await auth.reauthenticateWithCredential(this.currentUser.value, credentials);
    }
    return user;
  }

  public async confirmPasswordReset(oobCode: string, newPassword: string): Promise<void> {
    await auth.confirmPasswordReset(this.auth, oobCode, newPassword);
  }

  public async getEmailFromOob(oobCode: string): Promise<string | null> {
    const codeInfo = await auth.checkActionCode(this.auth, oobCode);
    return codeInfo?.data?.email ?? null;
  }

  public async verifyEmail(oobCode: string): Promise<void> {
    await auth.applyActionCode(this.auth, oobCode);
  }

  public async verifyCode(oobCode: string): Promise<auth.ActionCodeInfo> {
    const info = await auth.checkActionCode(this.auth, oobCode);
    return info;
  }

  /**
   * Get current user UID from auth state
   * @return {Observable<string | null>}
   * @warning used only in auth.guard.ts, in other cases use getCurrentUser$
   */
  public _getCurrentUserUid$(): Observable<string | null> {
    return auth.authState(this.auth).pipe(map((u) => u?.uid ?? null));
  }

  public getCurrentUser$(): Observable<auth.User | null> {
    return this.currentUser.asObservable();
  }

  public getCurrentUserUid(): string | null {
    return this.currentUser.value?.uid ?? null;
  }

  public getCurrentUserUidOnce(): Promise<string | null> {
    return AsyncHlp.onFirst(this._getCurrentUserUid$());
  }

  public getCurrentUserEmail(): string | null {
    return this.currentUser.value?.email ?? null;
  }

  public getCurrentUserName(): string | null {
    return this.currentUser.value?.displayName ?? null;
  }

  public isCurrentUserSuperadmin$(): Observable<boolean> {
    return this.claims.pipe(map((claims) => claims?.superadmin ?? false));
  }

  public updatePassword(newPassword: string): void {
    if (this.currentUser.value) auth.updatePassword(this.currentUser.value, newPassword);
  }

  /**
   * Request SSO access - adds authenticated user to pending list Called when user clicks "Request Access" button
   * @param email Domain email from login form (to ensure correct email is tracked)
   * @return {Promise<boolean>}
   */
  public requestSsoAccess(data: RequestAccessCtx): Promise<boolean> {
    return this.functionsApi.callFunction<boolean>('requestSsoAccess', data, (error) =>
      console.error('Error requesting SSO access:', error)
    );
  }

  /**
   * Check if email's domain is assigned to some SSO
   * @param {string} email
   * @return {Promise<CheckDomainRes | null>}
   */
  public checkSsoDomain(email: string): Promise<CheckDomainRes | null> {
    return this.functionsApi.callFunction<CheckDomainRes>('checkSsoDomain', email);
  }

  /**
   * Checks if user recently logs in; sort of HACK for activity update,
   * because after window reload it gets false, so it won't update activity
   * @return {boolean}
   */
  public hasLoggedAfterLoad(): boolean {
    return this._hasLoggedAfterLoad;
  }
}
